import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { track, captureUtm } from "@/lib/analytics";
import LeadMagnetSection from "@/components/LeadMagnetSection";
import FounderAccessPopup from "@/components/FounderAccessPopup";
import FounderSpotsTicker from "@/components/FounderSpotsTicker";
import ServicesCard from "@/components/ServicesCard";
import ServicesSocialProofStrip from "@/components/ServicesSocialProofStrip";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import {
  Building2,
  ArrowRight,
  Search,
  Calculator,
  LineChart,
  Users,
  Newspaper,
  Sparkles,
  Star,
  Check,
  TrendingUp,
  Home,
  KanbanSquare,
  Wallet,
  PiggyBank,
  MapPin,
  Clock,
  Compass,
  Facebook,
  Instagram,
  Youtube,
  Store,
  Award,
  BookOpen,
  Menu,
  X,
  Map as MapIcon,
  Bell,
  Eye,
  Share2,
  Gift,
  Flame,
  FileSignature,
  Trophy,
  Users2,
  Lock,
  Unlock,
  HandshakeIcon,
  Mail,
  DollarSign,
  Command,
  Pin,
  PhoneCall,
  MessageSquare,
  Brain,
  Inbox,
  PlayCircle,
  Headphones,
  Rocket,
  ShieldCheck,
  Phone,
  Gem,
  Truck,
  Crown,
  Zap,
} from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/8f41aff8-b5ca-41a3-a24c-e8ec600c2bfa/images/b52e4655a01a6215b0506b476ae4777fff26d76670f73fadba049943c40d9e3d.png";
const PROPERTY_IMAGES = [
  "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3OTQ3ODQ2MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1698994705178-d244d73ea573?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3OTQ3ODQ2MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3OTQ3ODQ2MHww&ixlib=rb-4.1.0&q=85",
];
// Distinct headshots so every testimonial has a unique face.
const AVATAR_DANIEL = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=faces&fit=crop&w=240&h=240&q=80";
const AVATAR_MAYA = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=faces&fit=crop&w=240&h=240&q=80";
const AVATAR_JASON = "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=faces&fit=crop&w=240&h=240&q=80";
const AVATAR_MARCUS = "https://images.unsplash.com/photo-1599566150163-29194dcaad36?crop=faces&fit=crop&w=240&h=240&q=80";
const AVATAR_SAMIR = "https://images.unsplash.com/photo-1542178243-bc20204b769f?crop=faces&fit=crop&w=240&h=240&q=80";
const AVATAR_TRENT = "https://images.unsplash.com/photo-1463453091185-61582044d556?crop=faces&fit=crop&w=240&h=240&q=80";

const MODULES = [
  { icon: Gem, title: "Cash Buyer Engine · 10M+ owners indexed",
    body: "Search every active cash buyer in any U.S. market. Wealth signals on every card: Millionaire flags, estimated net worth + income, BatchData sale-propensity score (0-99). Real building stats — beds, baths, sqft, year built — pulled from the deed.",
    cta: "/v3/crm/buyers/search" },
  { icon: Phone, title: "Apex 2.0 · The AI that answers every call",
    body: "Apex picks up inbound seller calls 24/7, runs your qualification script in a natural voice, books appointments on your Google Calendar, and auto-advances the lead through your pipeline as new signals arrive. Always-on, or after-hours only — your call.",
    cta: "/v3/settings" },
  { icon: Crown, title: "Wealth Intel · Prioritize the right buyers",
    body: "Stop dialing flippers when you have an institutional deal. Every buyer card surfaces net worth, household income, recently-moved flag, and a deed-history trail (flip count, average hold time, last sale price) so you call the buyer most likely to actually close.",
    cta: "/v3/crm/buyers/search" },
  { icon: Brain, title: "AI Lead Score + Best-Exit Verdict",
    body: "Every lead scored 0–100 with reasoning and a recommended next action. The 7-Strategy Analyzer (Wholesale · Novation · Sub-To · Wholetail · Flip · BRRRR · Rental) auto-ranks which exit puts the most cash in your pocket.",
    cta: "/v3/tools/analyzer" },
  { icon: Mail, title: "Omnichannel Sequences · SMS + Email + Tasks",
    body: "Drag-build multi-step drips combining SMS, email, and auto-tasks. Trigger on stage change, lead temperature, or buyer save. Auto-pause on a reply. Every step TCPA-screened before it fires.",
    cta: "/v3/crm/outreach/sequences" },
  { icon: ShieldCheck, title: "TCPA-Safe Outbound · Built-in DNC guard",
    body: "Every outbound SMS and voice call is screened against the National Do-Not-Call registry before send. Flag, block, override-with-consent — all immutably logged. Stop the $500–$1,500-per-violation TCPA bleed.",
    cta: "/v3/settings" },
  { icon: KanbanSquare, title: "Closing OS · Pipeline + DocuSign + Wins",
    body: "Drag-and-drop Kanban from New Lead → Under Contract → Closed. Send Assignment Contracts and PSAs from any card via DocuSign — auto-prefilled, status-tracked, signed PDFs land back in the app. Closed deals auto-publish to your branded Win Wall.",
    cta: "/v3/crm/pipeline" },
  { icon: Trophy, title: "Affiliate Engine · 45% recurring",
    body: "Refer a wholesaler, earn 45% recurring commission for the life of their account. Branded affiliate dashboard, Stripe payouts, and a leaderboard that publishes the top earners monthly.",
    cta: "/v3/growth/affiliate" },
  { icon: Store, title: "Deal Marketplace · JV with other operators",
    body: "Post your under-contract deals to the private operator marketplace. Vetted buyers browse, message, and JV directly — no 100-buyer blast emails, no chasing tire-kickers. Deals auto-expire when they close so your wall stays clean.",
    cta: "/v3/crm/marketplace" },
  { icon: Zap, title: "Dispo Studio · From contract to closed", badge: "NEW",
    body: "Private branded Deal Rooms per property. Buyers self-signup for match-alerts on their zips + price range. Offers submitted in-app with an anonymized auction leaderboard. Auto-counter engine. 1099 export. The dispo side finally caught up to the acquisitions side.",
    cta: "/dispo-studio" },
  { icon: BookOpen, title: "The Wholesaling Library · 15 books · $47 lifetime", badge: "NEW",
    body: "Every operator playbook Tracy has ever written — Wholesaling 101, the Cash-Buyer Blackbook, Seller Objections, the Call Coach, the Virtual Wholesaler, the Buyer Whisperer, and 9 more. 470+ pages. One flat $47. Lifetime access + all future updates.",
    cta: "/library-bundle" },
  { icon: Award, title: "Reader Streak · Gamified operator learning",
    body: "Every book you finish in the Wholesaling Library unlocks the next tier — Rookie → Apprentice → Wholesale Scholar → Deal Maker → Master Wholesaler. Because the operators who read close 3× more deals than the ones who wing it.",
    cta: "/library-bundle" },
];

const TESTIMONIALS = [
  { name: "Daniel Rossi", role: "Wholesaler · Austin",
    quote: "The Deal Finder + AI verdict combo is unfair. I now triage 30 leads a week into 3 that actually pencil — saves me 8 hours a week.",
    avatar: AVATAR_DANIEL },
  { name: "Maya Chen", role: "Wholesaler · Phoenix",
    quote: "Per-buyer photo tracking blew my mind. I can literally see which buyer is hot before they reply — auto-nudges close my deals 2× faster.",
    avatar: AVATAR_MAYA },
  { name: "Marcus Hale", role: "Flipper · Atlanta",
    quote: "Flipping was guesswork before. The ROI + repair guidance helped me pick winners and bump margins ~$11K per flip.",
    avatar: AVATAR_MARCUS },
];

const STATS = [
  { k: "10M+", v: "Cash buyers indexed" },
  { k: "$2.4B", v: "Property value analyzed" },
  { k: "297", v: "Pages of free playbooks" },
  { k: "24/7", v: "Apex AI answers calls" },
];

const FAQS = [
  { q: "What is iSpeedToClose?",
    a: "iSpeedToClose is the operating system for real estate wholesalers and investors. We combine a wholesale CRM, drag-and-drop pipeline, multi-strategy deal analyzer (Wholesale, Flip, BRRRR, Rental), portfolio tracker, market intelligence, and a power-team directory — all under one tab, with AI underwriting on every lead and Apex 2.0 answering your phone." },
  { q: "What is Apex 2.0?",
    a: "Apex 2.0 is the AI lead manager included free with Pro. It answers inbound seller calls 24/7, runs your qualification script (address, motivation, timeline, asking price, condition), books appointments straight onto your Google Calendar, and auto-advances the lead through your pipeline as new signals arrive. Always-on or after-hours only — your call. Costs ReSimpli users $300+/mo as an add-on; we include it." },
  { q: "Can I text and call leads from inside the app?",
    a: "Yes — both, on any paid tier. You have two options for the phone number: (1) buy an iSpeedToClose-managed number directly inside the app for $5/mo (no Twilio account required — pick by area code, ready in 30 seconds), or (2) bring your own Twilio account. SMS is two-way. Calling runs through our in-browser dialer with auto-recording + Whisper transcription + Claude grading within 60 seconds of hangup." },
  { q: "What about TCPA / Do-Not-Call compliance?",
    a: "TCPA-safe by default. Every outbound SMS and voice call gets screened against the National Do-Not-Call registry BEFORE it sends. If the destination is flagged, we block the send and log to an immutable audit trail. If you have prior express written consent, override with one click — the attestation is timestamped + saved for legal defense. We're the only wholesaler CRM that ships this out of the box." },
  { q: "Can I send contracts for e-signature?",
    a: "Yes. Send Assignment Contracts, Purchase & Sale Agreements, and JV splits directly from any Pipeline card via DocuSign. Merge fields auto-prefill, recipients sign on any device, status flows back live (Sent → Delivered → Completed), and the signed PDF lands back in the app. Completion auto-advances the lead to Under Contract." },
];

const POSTS = [
  { date: "FEB 12, 2026", title: "Turnkey vs. BRRRR: which strategy fits a first rental?",
    blurb: "Two opposite philosophies — one buys convenience, the other buys equity. We break down when each pencils and the math you should run before either." },
  { date: "FEB 8, 2026", title: "Wholesale → Hold pipeline: when to flip the script",
    blurb: "Sometimes the best assignment is the one you keep. Here's the back-of-envelope test we use before we send a deal to a cash buyer." },
  { date: "FEB 3, 2026", title: "How to underwrite a rental in 10 minutes (without spreadsheets)",
    blurb: "Most new investors fall in love before they run a single number. Our 6-input shortcut catches 80% of bad deals in the first 60 seconds." },
];

export default function Landing() {
  useEffect(() => { captureUtm(); }, []);
  return (
    <div className="mkt-shell">
      <Nav />
      <Hero />
      <SocialProof />
      <DemoModeBanner />
      <Modules />
      <Showcase />
      <Stats />
      <Testimonials />
      <LeadMagnetSection />
      <Pricing />
      <ReferralStrip />
      <FAQ />
      <CTA />
      <Footer />
      <FounderAccessPopup />
    </div>
  );
}

function DemoModeBanner() {
  return (
    <section className="bg-white py-14 border-y border-[var(--line)]" data-testid="demo-mode-banner">
      <div className="max-w-[1240px] mx-auto px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--ink)] via-[#0F172A] to-[#0B1220] text-white p-8 lg:p-12">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[var(--primary)]/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[var(--primary)] bg-[var(--primary)]/15 border border-[var(--primary)]/30 rounded-full px-3 py-1 mb-4">
                <Rocket size={11} /> Try it in 30 seconds · No credit card
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-4">
                Sign up and land in a <span className="text-[var(--primary)]">fully-loaded</span> workspace.
              </h2>
              <p className="text-white/75 text-lg leading-relaxed mb-6 max-w-xl">
                The second you create your account you drop into Demo Mode — 6 sample leads in the pipeline,
                3 graded call recordings, a contract sample, and an 8-step Guided Tour that walks you
                through every module. Clear the demo data in one click when you&apos;re ready to load your own.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/register" className="btn-money" data-testid="demo-mode-cta-register">
                  Start the tour — free <ArrowRight size={14} />
                </Link>
                <Link to="/login" className="btn-ghost text-white border-white/30 hover:bg-white/10" data-testid="demo-mode-cta-login">
                  I already have an account
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DemoStat icon={KanbanSquare} k="6" v="Sample leads in the pipeline" />
              <DemoStat icon={Headphones} k="3" v="Pre-scored call recordings" accent />
              <DemoStat icon={FileSignature} k="1" v="DocuSign envelope in flight" />
              <DemoStat icon={PlayCircle} k="8 steps" v="Guided tour, dismissable" accent />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoStat({ icon: Icon, k, v, accent }) {
  return (
    <div className={"rounded-xl p-4 border " + (accent ? "bg-white/10 border-[var(--primary)]/40" : "bg-white/5 border-white/15")}>
      <Icon size={16} className={accent ? "text-[var(--primary)]" : "text-white/70"} />
      <div className={"font-display font-black text-2xl tracking-tighter mt-2 " + (accent ? "text-[var(--primary)]" : "text-white")}>{k}</div>
      <div className="text-[11px] text-white/65 mt-1 leading-snug">{v}</div>
    </div>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  const LINKS = [
    { href: "#modules", label: "Platform" },
    { href: "#markets", label: "Markets" },
    { href: "#pricing", label: "Pricing" },
    { to: "/resources", label: "Resources" },
    { to: "/compare", label: "Compare" },
    { href: "#field-notes", label: "Field Notes" },
    { href: "#faqs", label: "FAQs" },
  ];
  return (
    <header className="mkt-nav">
      <div className="mkt-nav-inner">
        <Link to="/" className="flex items-center" data-testid="mkt-brand">
          <img
            src="/brand/wordmark-color.png"
            alt="iSpeedToClose"
            className="h-14 md:h-16 w-auto"
            width="340"
            height="64"
          />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[var(--ink-2)]">
          {LINKS.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} className="hover:text-[var(--ink)]" data-testid={`mkt-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} className="hover:text-[var(--ink)]" data-testid={`mkt-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</a>
            )
          )}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="btn-ghost" data-testid="mkt-login-button">Sign in</Link>
          <Link to="/register" className="btn-money" data-testid="mkt-cta-register">
            Sign up free <ArrowRight size={14} />
          </Link>
        </div>
        <button
          type="button"
          className="md:hidden p-2 rounded-lg hover:bg-black/5"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mkt-mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-black/5 bg-white" data-testid="mkt-mobile-menu">
          <nav className="px-6 py-4 flex flex-col gap-3 text-sm font-medium text-[var(--ink)]">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  className="py-1.5"
                  onClick={() => setOpen(false)}
                  data-testid={`mkt-mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                >{l.label}</Link>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  className="py-1.5"
                  onClick={() => setOpen(false)}
                  data-testid={`mkt-mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                >{l.label}</a>
              )
            )}
            <div className="flex flex-col gap-2 pt-3 border-t border-black/5">
              <Link to="/login" className="btn-ghost" onClick={() => setOpen(false)}>Sign in</Link>
              <Link to="/register" className="btn-money" onClick={() => setOpen(false)}>
                Sign up free <ArrowRight size={14} />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="mkt-hero">
      <div className="mkt-hero-bg" style={{ backgroundImage: `url('${HERO_BG}')` }} />
      <div className="mkt-hero-inner">
        <div>
          <FounderSpotsTicker className="mb-3" />
          <div className="mkt-eyebrow">
            <Gem size={11} className="inline mr-1.5 -mt-[1px] text-[var(--primary)]" /> NEW · AI Lead Scoring + Property Valuation + Call Coach
          </div>
          <div
            className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold uppercase tracking-wider text-emerald-800"
            data-testid="hero-tcpa-badge"
            title="Outbound SMS + Voice screened against the National DNC registry before sending. Audit-logged consent overrides for legal defense."
          >
            <ShieldCheck size={11} className="text-emerald-600" />
            TCPA-Safe by default — no $1,500/violation surprises
          </div>
          <h1>
            Find the buyer.<br />
            <em>Score</em> the seller.<br />
            Close the deal.
          </h1>
          <p className="lead">
            Claude scores every lead 0-100, calculates ARV + your max offer in 5 seconds,
            and grades every recorded call with a 0-100 coach score. Plus the full wholesale
            stack: 10M+ cash-buyer engine, CRM, dialer, contracts, and a curated Deals Inbox —
            under one tab.
          </p>

          {/* AI features strip */}
          <div className="grid grid-cols-3 gap-2 mt-5 max-w-xl" data-testid="hero-ai-strip">
            <AIFeaturePill emoji="🔥" title="AI Lead Score" sub="0-100 + reasoning" />
            <AIFeaturePill emoji="🧮" title="ARV + MAO" sub="70% rule in 5 sec" />
            <AIFeaturePill emoji="📞" title="Apex 2.0" sub="AI answers every call" />
          </div>

          <form
            className="mt-6 max-w-xl"
            onSubmit={(e) => {
              e.preventDefault();
              const v = e.currentTarget.elements?.hero_address?.value?.trim();
              track("magic_hero_submitted", {
                source: v ? "manual_typed" : "empty",
                length: (v || "").length,
              });
              const dest = v ? `/register?address=${encodeURIComponent(v)}` : "/register";
              window.location.href = dest;
            }}
          >
            <AddressAutocomplete
              name="hero_address"
              placeholder="Enter a property address to analyze it for free…"
              inputClassName="w-full pl-12 pr-44 py-4 rounded-xl bg-white text-[var(--ink)] text-base outline-none border border-[var(--line)] placeholder:text-[var(--ink-3)] shadow-sm"
              dataTestid="hero-search-input"
              onChange={(v) => {
                if ((v || "").length === 3) track("magic_hero_typed");
              }}
              onSelect={(label) => {
                track("magic_hero_picked", { address: label });
                track("magic_hero_submitted", { source: "autocomplete_pick" });
                window.location.href = `/register?address=${encodeURIComponent(label)}`;
              }}
              cta={
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 btn-money !py-2 !px-4 text-sm"
                  data-testid="hero-search-cta"
                >
                  Analyze it <ArrowRight size={14} />
                </button>
              }
            />
            <div className="text-xs text-[var(--ink-3)] mt-2">No credit card required · 14-day full access · Land in a fully-loaded Demo Workspace the second you sign up</div>
          </form>

          <div className="flex items-center gap-3 mt-8 text-[var(--ink-2)] text-sm">
            <Star size={14} className="text-[var(--primary)]" fill="currentColor" />
            <Star size={14} className="text-[var(--primary)]" fill="currentColor" />
            <Star size={14} className="text-[var(--primary)]" fill="currentColor" />
            <Star size={14} className="text-[var(--primary)]" fill="currentColor" />
            <Star size={14} className="text-[var(--primary)]" fill="currentColor" />
            <span>Built by an operator, for operators</span>
          </div>
        </div>

        <HeroAside />
      </div>
    </section>
  );
}


function AIFeaturePill({ emoji, title, sub }) {
  return (
    <div
      className="inline-flex flex-col items-start gap-0.5 px-3 py-2 rounded-xl bg-white border border-emerald-200 shadow-sm"
      data-testid={`hero-ai-pill-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-base leading-none">{emoji}</span>
        <span className="font-display font-bold text-xs text-stone-900 leading-tight">{title}</span>
      </div>
      <span className="text-[10px] text-stone-500 leading-tight ml-[22px]">{sub}</span>
    </div>
  );
}


function HeroAside() {
  return (
    <div className="relative">
      <div className="glass">
        <div className="flex items-center justify-between mb-3">
          <span className="label-eyebrow">
            Deal-of-the-Day · 3315 Hawthorne St
          </span>
          <span className="chip" style={{ background: "rgba(16,185,129,0.12)", color: "var(--primary-dark)", borderColor: "rgba(16,185,129,0.35)" }}>
            <span className="chip-dot" style={{ background: "var(--primary)" }} /> GO
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Metric label="ARV" value="$235,000" />
          <Metric label="MAO (70%)" value="$126,500" accent />
          <Metric label="Repairs" value="$28,000" />
          <Metric label="Profit" value="+$105,000" accent />
        </div>
        <p className="text-sm text-[var(--ink-2)] leading-relaxed">
          $34.5K below the 70% rule — strong margin even after $10K assignment fee. Out-of-state heir,
          motivated. Pull comps + line up contractor today.
        </p>
      </div>
      <div className="glass mt-4 grid grid-cols-3 gap-3">
        <MiniStat label="Active leads" value="6" />
        <MiniStat label="Pipeline value" value="$60K" accent />
        <MiniStat label="Closed YTD" value="11" />
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="bg-[var(--bg)] rounded-lg p-3 border border-[var(--line)]">
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1 font-semibold">{label}</div>
      <div className={"font-display font-bold text-lg " + (accent ? "text-[var(--primary-dark)]" : "text-[var(--ink)]")}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1 font-semibold">{label}</div>
      <div className={"font-display font-bold text-2xl tracking-tight " + (accent ? "text-[var(--primary-dark)]" : "text-[var(--ink)]")}>{value}</div>
    </div>
  );
}

function SocialProof() {
  return (
    <div className="border-y border-[var(--line)] bg-white">
      <div className="max-w-[1240px] mx-auto px-8 py-8 flex flex-wrap items-center justify-between gap-6 text-[var(--ink-2)]">
        <span className="text-xs uppercase tracking-widest font-semibold">Trusted by investors hustling in</span>
        <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm font-semibold opacity-80">
          {["Austin", "Phoenix", "Tampa", "Charlotte", "Columbus", "Denver", "Memphis", "Atlanta"].map((c) => <span key={c}>{c}</span>)}
        </div>
      </div>
    </div>
  );
}

function Modules() {
  return (
    <section className="mkt-section" id="modules">
      <div className="grid md:grid-cols-2 gap-8 items-end mb-14">
        <div>
          <div className="mkt-eyebrow">The platform</div>
          <h2 className="mkt-h2">Twenty-five modules. Zero spreadsheets.</h2>
        </div>
        <p className="text-[var(--ink-2)] text-lg">
          From the inbound seller call to the wired assignment fee — every step lives in iSpeedToClose, with <strong>Apex 2.0</strong> answering your phone, AI underwriting every deal, and per-buyer engagement tracking baked in.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {MODULES.map((m) => (
          <Link
            to={m.cta}
            key={m.title}
            className="mkt-feature relative overflow-hidden group"
            data-testid={`module-${m.title.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="w-11 h-11 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center mb-5">
              <m.icon size={20} />
            </div>
            {m.badge && (
              <span className="absolute top-4 right-4 bg-[var(--primary)] text-[var(--ink)] text-[9px] font-black tracking-widest uppercase px-2 py-1 rounded-md">
                {m.badge}
              </span>
            )}
            <h3 className="font-display font-bold text-2xl mb-2">{m.title}</h3>
            <p className="text-[var(--ink-2)] leading-relaxed mb-5">{m.body}</p>
            <div className="mt-auto text-sm font-semibold text-[var(--ink)] flex items-center gap-2">
              Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── Showcase (alternating DealCheck-style feature blocks) ─────────────────
const SHOWCASES = [
  {
    eyebrow: "Cash Buyer Engine",
    title: "10 million cash buyers. One search.",
    body: "Search every active cash buyer in any U.S. metro by buy-box, market, and portfolio size. Every card surfaces wealth signals — Millionaire flags, estimated net worth, household income — and a BatchData propensity score so you call the buyer most likely to close, not the cheapest lead.",
    bullets: [
      "Millionaire badge + net-worth bucket on every card",
      "Sale propensity score (0-99) ranks Hot · Warm · Cold",
      "Recently-moved flag highlights active investors",
    ],
    cta: "Search Cash Buyers →",
    href: "/v3/crm/buyers/search",
    Mockup: CashBuyerMockup,
  },
  {
    eyebrow: "Apex 2.0",
    title: "The AI that answers, books, and advances every deal.",
    body: "Inbound calls hit Apex 24/7. It picks up in a natural voice, qualifies the seller — address, motivation, timeline, asking price, condition — books the appointment on your Google Calendar, and as new SMS replies arrive Claude auto-advances the lead through your pipeline. You wake up to a stage='Under Contract' card on what was a New Lead yesterday.",
    bullets: [
      "Always-on, or after-hours-only — flip one toggle",
      "Books appointments directly on Google Calendar after qualifying",
      "Auto-advances the lead stage as new signals arrive (one-click undo)",
    ],
    cta: "Configure Apex →",
    href: "/v3/settings",
    Mockup: Apex20Mockup,
  },
  {
    eyebrow: "Pipeline",
    title: "Drag-and-drop your dispo.",
    body: "Every lead lives on a Kanban — New Lead → Contacted → Negotiating → Offer Sent → Appointment Set → Under Contract → Closed. Best-Exit pill, ARV, and assignment fee surface on every card. With Apex 2.0 watching, leads move themselves when the signal is clear.",
    bullets: ["Auto-stages on signed contracts via DocuSign", "Lead temperature & last-touch on every card", "Apex 2.0 auto-advances stages on inbound SMS + calls"],
    cta: "See the Pipeline →",
    href: "/v3/crm/pipeline",
    Mockup: PipelineMockup,
  },
];

function Showcase() {
  return (
    <section className="bg-[var(--bg)] py-24" id="showcase">
      <div className="max-w-[1240px] mx-auto px-8">
        <div className="grid md:grid-cols-2 gap-8 items-end mb-20">
          <div>
            <div className="mkt-eyebrow">A closer look</div>
            <h2 className="mkt-h2">Built for wholesalers — every pixel.</h2>
          </div>
          <p className="text-[var(--ink-2)] text-lg">
            Forget cobbled-together tabs. iSpeedToClose pulls the AI Call Coach, Discover, the Analyzer, your Pipeline, ⌘K search, and per-buyer engagement into a single, fast, native workspace.
          </p>
        </div>

        <div className="space-y-28">
          {SHOWCASES.map((s, idx) => {
            const reverse = idx % 2 === 1;
            const M = s.Mockup;
            return (
              <div key={s.title}>
                <div
                  className={`grid md:grid-cols-2 gap-16 items-center ${reverse ? "md:[direction:rtl]" : ""}`}
                  data-testid={`showcase-${s.eyebrow.toLowerCase()}`}
                >
                <div className="md:[direction:ltr]">
                  <div className="mkt-eyebrow">{s.eyebrow}</div>
                  <h3 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3 mb-5 leading-[1.05] text-[var(--ink)]">
                    {s.title}
                  </h3>
                  <p className="text-[var(--ink-2)] text-lg leading-relaxed mb-6">{s.body}</p>
                  <ul className="space-y-3 mb-7">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[var(--ink)]">
                        <span className="mt-[6px] w-5 h-5 rounded-full bg-[var(--primary)]/12 text-[var(--primary-dark)] flex items-center justify-center flex-shrink-0">
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={s.href}
                    className="btn-outline inline-flex items-center gap-2"
                    data-testid={`showcase-cta-${s.eyebrow.toLowerCase()}`}
                  >
                    {s.cta}
                  </Link>
                </div>
                <div className="md:[direction:ltr]">
                  <M />
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MockupShell({ children, label }) {
  return (
    <div className="showcase-mockup">
      <div className="showcase-chrome">
        <span className="showcase-dot" style={{ background: "#EF4444" }} />
        <span className="showcase-dot" style={{ background: "#F59E0B" }} />
        <span className="showcase-dot" style={{ background: "#10B981" }} />
        <span className="text-xs text-[var(--ink-3)] font-medium ml-3">{label}</span>
      </div>
      <div className="showcase-body">{children}</div>
    </div>
  );
}

function CashBuyerMockup() {
  const cards = [
    {
      name: "Granite Holdings LLC",
      principal: "Mike Johnson",
      market: "Dallas, TX",
      score: 92,
      tone: "Hot",
      props: 18,
      avgBuy: "$95k",
      netWorth: "$2.1M",
      income: "$240k",
      millionaire: true,
      moved: false,
      flips: 3,
    },
    {
      name: "Sarah Miller",
      principal: null,
      market: "Atlanta, GA",
      score: 87,
      tone: "Hot",
      props: 12,
      avgBuy: "$125k",
      netWorth: "$1.4M",
      income: "$185k",
      millionaire: true,
      moved: true,
      flips: 1,
    },
    {
      name: "Summit Property Group",
      principal: "David Chen",
      market: "Phoenix, AZ",
      score: 71,
      tone: "Warm",
      props: 28,
      avgBuy: "$140k",
      netWorth: "$3.5M",
      income: "$312k",
      millionaire: true,
      moved: false,
      flips: 7,
    },
  ];
  return (
    <MockupShell label="ispeedtoclose.com / app / buyers / search">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-semibold">
            Search · Dallas, TX
          </div>
          <div className="font-display font-bold text-lg text-[var(--ink)]">
            3 of 1,247 cash buyers
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">
          Sorted by propensity
        </div>
      </div>

      <div className="space-y-3">
        {cards.map((c) => (
          <div
            key={c.name}
            className="p-3 rounded-lg border border-[var(--line)] bg-white"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-display font-bold text-sm text-[var(--ink)] truncate">
                    {c.name}
                  </span>
                  {c.millionaire && (
                    <span className="inline-flex items-center gap-0.5 text-[8px] uppercase font-bold tracking-wider px-1.5 py-[2px] rounded bg-gradient-to-r from-amber-400 to-yellow-500 text-white">
                      <Gem size={8} /> Millionaire
                    </span>
                  )}
                </div>
                {c.principal && (
                  <div className="text-[10px] text-[var(--ink-3)]">
                    Principal: <strong>{c.principal}</strong>
                  </div>
                )}
              </div>
              <div
                className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-[2px] rounded border whitespace-nowrap ${
                  c.tone === "Hot"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}
              >
                <Flame size={8} className="inline -mt-0.5 mr-0.5" />
                {c.tone} · {c.score}
              </div>
            </div>

            <div className="flex flex-wrap gap-1 mb-2">
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-[2px] rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                <Wallet size={8} className="inline -mt-0.5 mr-0.5" /> NW {c.netWorth}
              </span>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-[2px] rounded bg-sky-50 text-sky-800 border border-sky-200">
                <DollarSign size={8} className="inline -mt-0.5" /> {c.income}
              </span>
              {c.moved && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-[2px] rounded bg-violet-50 text-violet-800 border border-violet-200">
                  <Truck size={8} className="inline -mt-0.5 mr-0.5" /> Recently moved
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="text-[9px]">
                <div className="text-[var(--ink-3)] uppercase font-semibold">Props</div>
                <div className="font-display font-bold text-[var(--ink)] text-xs">{c.props}</div>
              </div>
              <div className="text-[9px]">
                <div className="text-[var(--ink-3)] uppercase font-semibold">Avg buy</div>
                <div className="font-display font-bold text-[var(--ink)] text-xs">{c.avgBuy}</div>
              </div>
              <div className="text-[9px]">
                <div className="text-[var(--ink-3)] uppercase font-semibold">{c.market.split(",")[0]}</div>
                <div className="font-display font-bold text-[var(--ink)] text-xs">{c.flips} flips</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </MockupShell>
  );
}

function Apex20Mockup() {
  // Snapshot of a live Apex 2.0 inbound seller call — the transcript
  // streams in turn-by-turn on the left; the auto-generated lead card
  // (verdict + score + slots) appears on the right the moment the AI
  // detects all five slots are covered. Inspired by the actual API
  // response shape returned by /api/apex/test.
  const turns = [
    { role: "assistant", text: "Hi! Thanks for calling. What's the address of the property you're looking to sell?" },
    { role: "seller", text: "4521 Cedar Lane, Dallas, Texas — 75204." },
    { role: "assistant", text: "Got it — Cedar Lane in Dallas. What's making you think about selling right now?" },
    { role: "seller", text: "Going through a divorce. Need to move out fast." },
    { role: "assistant", text: "Sorry to hear that. How soon do you need to close?" },
    { role: "seller", text: "Within 30 days if possible." },
    { role: "assistant", text: "Understood. What are you hoping to get for the property?" },
    { role: "seller", text: "Around 180 thousand." },
    { role: "assistant", text: "And what kind of shape is it in?" },
    { role: "seller", text: "Decent — needs paint and the kitchen could use an update." },
  ];

  return (
    <MockupShell label="ispeedtoclose.com · live Apex call · CallSid CA8f2…d4e1">
      <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-[var(--line)]">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold flex items-center gap-1">
            <Sparkles size={10} /> Apex 2.0 · Claude Sonnet 4.6
          </div>
          <div className="font-display font-bold text-base text-[var(--ink)] truncate">
            Inbound call · (214) 555-0142
          </div>
          <div className="text-[10px] text-[var(--ink-3)] flex items-center gap-1.5">
            <span className="inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live · 1:47 · 5/5 slots captured
          </div>
        </div>
        <div className="text-[9px] uppercase tracking-widest text-[var(--ink-3)] font-bold flex items-center gap-1 whitespace-nowrap">
          <Phone size={9} className="text-indigo-600" /> Polly · Joanna
        </div>
      </div>

      {/* Transcript */}
      <div className="space-y-1.5 mb-3">
        {turns.map((t, i) => (
          <div
            key={i}
            className={`flex ${t.role === "seller" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] text-[11px] leading-snug px-2.5 py-1.5 rounded-lg ${
                t.role === "assistant"
                  ? "bg-indigo-50 border border-indigo-100 text-[var(--ink)]"
                  : "bg-slate-100 border border-slate-200 text-[var(--ink)]"
              }`}
            >
              <div className="text-[9px] uppercase tracking-widest font-bold opacity-60 mb-0.5">
                {t.role === "assistant" ? "Apex" : "Seller"}
              </div>
              {t.text}
            </div>
          </div>
        ))}
      </div>

      {/* Auto-generated lead card */}
      <div className="mt-3 pt-3 border-t border-[var(--line)]">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-2 flex items-center gap-1">
          <Sparkles size={10} /> Auto-generated lead · stamped to pipeline
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-50/20 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm text-[var(--ink)] truncate">4521 Cedar Lane</div>
              <div className="text-[10px] text-[var(--ink-3)]">Dallas, TX 75204 · stage: New Lead</div>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-600 text-white">
              GO · 91
            </span>
          </div>
          <div className="text-[11px] text-[var(--ink-2)] italic leading-snug mb-2">
            &ldquo;Motivated divorce seller in Dallas, needs to close within 30 days, asking $180K on a cosmetically challenged property.&rdquo;
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
            <div><span className="text-[var(--ink-3)]">Motivation:</span> <strong>Divorce</strong></div>
            <div><span className="text-[var(--ink-3)]">Timeline:</span> <strong>30 days</strong></div>
            <div><span className="text-[var(--ink-3)]">Asking:</span> <strong>$180,000</strong></div>
            <div><span className="text-[var(--ink-3)]">Condition:</span> <strong>Light rehab</strong></div>
          </div>
        </div>

        {/* Phase 3a — auto-stage-move chip */}
        <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/60">
          <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center">
            <Sparkles size={10} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
              Stage auto-advanced
            </div>
            <div className="text-[11px] text-[var(--ink)] font-bold">
              New Lead → <span className="text-emerald-700">Appointment Set</span>
            </div>
            <div className="text-[9px] text-[var(--ink-3)]">
              Apex booked Wed 2:00 PM · 97% confidence · one-click undo
            </div>
          </div>
        </div>
      </div>
    </MockupShell>
  );
}

function PipelineMockup() {
  const cols = [
    { name: "New Lead",  tone: "#94A3B8", count: 12, cards: [{ addr: "1428 Elm St",       price: "$142k", verdict: "GO" }] },
    { name: "Analyzed",  tone: "#0EA5E9", count: 7,  cards: [{ addr: "27 Riverbend Dr",   price: "$175k", verdict: "GO" }] },
    { name: "Contract",  tone: "#F59E0B", count: 3,  cards: [{ addr: "904 Oak Ave",       price: "$118k", verdict: "MAYBE" }] },
    { name: "Closed",    tone: "#10B981", count: 11, cards: [{ addr: "85 Sunset Blvd",    price: "$99k",  verdict: "GO" }] },
  ];
  return (
    <MockupShell label="ispeedtoclose.com / app / pipeline">
      <div className="grid grid-cols-4 gap-2">
        {cols.map((c) => (
          <div key={c.name} className="bg-[var(--bg)] border border-[var(--line)] rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.tone }} />
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)]">{c.name}</span>
              </div>
              <span className="text-[10px] text-[var(--ink-3)] font-semibold">{c.count}</span>
            </div>
            {c.cards.map((card) => (
              <div key={card.addr} className="bg-white border border-[var(--line)] rounded-md p-2 mb-2 shadow-sm">
                <div className="text-[11px] font-semibold text-[var(--ink)] leading-tight">{card.addr}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-[var(--ink-2)]">{card.price}</span>
                  <span className={"text-[9px] font-bold px-1.5 py-[1px] rounded-full text-white"} style={{ background: card.verdict === "GO" ? "#10B981" : "#F59E0B" }}>{card.verdict}</span>
                </div>
              </div>
            ))}
            <div className="h-12 border border-dashed border-[var(--line)] rounded-md" />
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="text-center p-2 rounded-lg bg-[var(--bg)] border border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Pipeline</div>
          <div className="font-display font-bold text-sm text-[var(--ink)]">$534k</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-[var(--bg)] border border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Avg cycle</div>
          <div className="font-display font-bold text-sm text-[var(--ink)]">11 days</div>
        </div>
        <div className="text-center p-2 rounded-lg bg-[var(--bg)] border border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Win rate</div>
          <div className="font-display font-bold text-sm text-[var(--primary-dark)]">38%</div>
        </div>
      </div>
    </MockupShell>
  );
}

function Stats() {
  return (
    <section className="bg-[var(--ink)] text-white relative overflow-hidden" id="markets">
      <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ backgroundImage: `url('${HERO_BG}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--ink)]/85 to-[var(--ink)]" />
      <div className="relative max-w-[1240px] mx-auto px-8 py-24">
        <div className="grid md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.k}>
              <div className="font-display font-black text-5xl tracking-tighter text-[var(--primary)] mb-2">{s.k}</div>
              <div className="text-white/70 leading-relaxed">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="mkt-section">
      <div className="grid md:grid-cols-2 gap-8 items-end mb-14">
        <div>
          <div className="mkt-eyebrow">Field notes</div>
          <h2 className="mkt-h2">Wholesalers + investors hustling with iSpeedToClose.</h2>
        </div>
        <p className="text-[var(--ink-2)] text-lg">
          Built with operators in the trenches — every feature comes from a real deal flow problem.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t) => (
          <div key={t.name} className="card hover:shadow-lg transition-shadow" data-testid={`testimonial-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex gap-1 mb-3">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={14} className="text-[var(--primary)]" fill="currentColor" />)}
            </div>
            <p className="text-[15px] leading-relaxed mb-5">{t.quote}</p>
            <div className="flex items-center gap-3 pt-4 border-t border-[var(--line)]">
              <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
              <div>
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-[var(--ink-2)]">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const PRICING = [
  { name: "Basic", price: 97, annual: 970, trial: 14, blurb: "CRM essentials + Dispo Studio Presentation",
    items: ["Unlimited leads + Pipeline CRM", "Two-way SMS + Click-to-Call (BYO Twilio)", "Always-On Call Recording", "20 BatchData skip-trace credits / mo", "Lead Marketplace browse + buy packs", "Full eBook library + 30-day Trash Bin", "🆕 Dispo Studio · Presentation — 5 Deal Rooms, public buyer signup page", "TCPA-Safe DNC screening", "1 saved search + 1 drip campaign", "Email support"] },
  { name: "Pro", price: 197, annual: 1970, popular: true, trial: 14, blurb: "AI-powered wholesaling OS · Apex 2.0 + Full Dispo Studio",
    items: ["Everything in Basic", "📞 Apex 2.0 — AI answers every call & books appointments (Google Calendar synced · 24/7 or after-hours)", "🆕 Dispo Studio · Automation — unlimited Deal Rooms, match notifications, in-app offer auction, auto-counter, 1099 export", "🔥 AI Lead Scoring — 0-100 with reasoning", "🧮 AI Property Valuation + MAO (70% rule)", "📞 AI Call Summary + 0-100 Coach Score", "AI Rehab Scan (10/day · Claude Vision)", "AI-Vetted Deals Inbox + real-time SMS alerts", "Distress filters + auto-enrichment on imports", "Unlimited saved searches, captures, drips, sequences", "100 BatchData skip-trace credits / mo", "Affiliate program — 45% recurring commission", "Priority support (24h SLA)"] },
  { name: "Team", price: 397, annual: 3970, blurb: "Multi-seat / brokerage",
    items: ["Everything in Pro", "5 team seats included", "Additional seats: $39/mo (prorated)", "VA seats: $19/mo (restricted role)", "250 skip-trace credits / mo · 30/day AI Rehab Scans", "Shared workspace lists + Team Analytics", "Role-based permissions (Owner / Admin / Member / VA)", "Custom sender domain · API access", "Contract automation (PDF)", "Dedicated Slack + 4h SLA + Onboarding call"] },
];


// ─── Three Ways to Sign — differentiator block ─────────────────────────────
// ─── Community Deal-Sharing Wall — landing block ────────────────────────────
const COMMUNITY_STEPS = [
  {
    n: "01",
    badge: "Share",
    icon: Users2,
    title: "Publish a masked version of any dead lead",
    body: "Wrong market? Wrong type? Numbers off by $10k? Flip the share toggle on your lead and a privacy-safe card appears on the community wall — street name blurred, your contact info hidden, ARV and asking fee front and center.",
  },
  {
    n: "02",
    badge: "Bid",
    icon: HandshakeIcon,
    title: "Other wholesalers tap “I'll take it”",
    body: "Buyers in your state see your card, send you a short pitch (cash strength, close window, EMD speed). You pick the strongest — community reputation is right next to their name so you know who's actually closed deals.",
  },
  {
    n: "03",
    badge: "Reveal",
    icon: Unlock,
    title: "Accept once → address unlocks + DocuSign drafts itself",
    body: "The moment you tap Accept, the listing closes, all other bidders are auto-declined, the real address unlocks for the winning buyer only, and a pre-filled Assignment of Contract envelope appears in your Contracts page. One click to send.",
  },
];



function Pricing() {
  const [annual, setAnnual] = useState(false);
  useEffect(() => { track("pricing_view"); }, []);
  return (
    <section className="mkt-section" id="pricing">
      <div className="max-w-2xl mb-10">
        <div className="mkt-eyebrow">Pricing</div>
        <h2 className="mkt-h2">Pays for itself on your first assignment.</h2>
        <p className="text-[var(--ink-2)] text-lg">14-day free trial on Pro. No setup fees. Cancel any time.</p>
      </div>
      <div className="flex items-center justify-center gap-3 mb-10">
        <button
          type="button"
          onClick={() => setAnnual(false)}
          className={"btn " + (!annual ? "btn-primary" : "btn-ghost")}
          data-testid="pricing-cycle-monthly"
        >Monthly</button>
        <button
          type="button"
          onClick={() => setAnnual(true)}
          className={"btn " + (annual ? "btn-primary" : "btn-ghost")}
          data-testid="pricing-cycle-annual"
        >
          Annual <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">2 months free</span>
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PRICING.map((p) => {
          const isFree = p.price === 0;
          const displayPrice = annual ? p.annual : p.price;
          const monthlyEq = annual && p.price ? Math.round(p.annual / 12) : null;
          return (
            <div key={p.name} className={"price-card relative" + (p.popular ? " popular" : "") + (p.comingSoon ? " opacity-95" : "")} data-testid={`pricing-card-${p.name.toLowerCase()}`}>
              {p.comingSoon && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-[0.18em] bg-amber-100 text-amber-800 border border-amber-300 shadow-sm z-10"
                  data-testid={`pricing-coming-soon-badge-${p.name.toLowerCase()}`}
                >
                  Coming soon
                </div>
              )}
              {p.popular && <span className="popular-tag">Most popular</span>}
              <div className="font-display font-bold text-2xl mb-1">{p.name}</div>
              <div className="text-[var(--ink-2)] text-sm mb-5">{p.blurb}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="price">${displayPrice.toLocaleString()}</span>
                {!isFree && <span className="text-[var(--ink-2)]">{annual ? "/yr" : "/mo"}</span>}
              </div>
              <div className="text-xs text-[var(--ink-3)] mb-5 min-h-[18px]">
                {monthlyEq ? `${"$" + monthlyEq}/mo billed annually` : ""}
                {p.trial && !annual && <span className="ml-1 text-emerald-700 font-semibold">· {p.trial}-day free trial</span>}
              </div>
              <ul className="space-y-2.5 mb-7 text-sm">
                {p.items.map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 text-[var(--primary)] shrink-0" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
              {p.comingSoon ? (
                <button
                  type="button"
                  className="btn-primary mt-auto opacity-60 cursor-not-allowed"
                  disabled
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  title="Multi-seat workspaces are in active development — join the waitlist below."
                >
                  Coming soon
                </button>
              ) : (
                <Link
                  to={isFree ? "/register" : `/register?tier=${p.name.toLowerCase()}&cycle=${annual ? "annual" : "monthly"}`}
                  className={p.popular ? "btn-money mt-auto" : "btn-primary mt-auto"}
                  onClick={() => track("pricing_click", { tier: p.name.toLowerCase(), cycle: annual ? "annual" : "monthly" })}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                >
                  {isFree ? "Start free" : `Start ${p.name}`} <ArrowRight size={14} />
                </Link>
              )}
            </div>
          );
        })}
        <ServicesCard />
      </div>
      <ServicesSocialProofStrip />
    </section>
  );
}

function ReferralStrip() {
  return (
    <section className="mkt-section" style={{ paddingTop: 0 }} id="referrals">
      <div
        className="relative overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-10 lg:p-14"
        data-testid="landing-referral-strip"
      >
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-300/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-12 w-72 h-72 rounded-full bg-rose-300/25 blur-3xl pointer-events-none" />
        <div className="relative grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700 bg-amber-100/80 border border-amber-200 rounded-full px-3 py-1 mb-4">
              <Gift size={12} /> Share via text · earn Stripe credit
            </div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter text-[var(--ink)] leading-[1.05] mb-3">
              Refer another wholesaler. <em className="text-amber-700 not-italic">Both of you pocket a free month.</em>
            </h2>
            <p className="text-[var(--ink-2)] text-lg leading-relaxed mb-6 max-w-xl">
              One-tap text share from your phone fires a beautifully unfurled invite to your contacts. When they
              upgrade, we drop a real Stripe credit on both accounts — auto-applied on the next billing cycle.
              No coupon codes, no waiting on support.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="btn-money" data-testid="landing-referral-cta-register">
                Try free <ArrowRight size={14} />
              </Link>
              <Link
                to="/leaderboards/streaks"
                className="btn-outline inline-flex items-center gap-2"
                data-testid="landing-referral-cta-leaderboard"
              >
                <Flame size={14} className="text-amber-600" /> See live Hot Streak leaderboard
              </Link>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="grid grid-cols-2 gap-3">
              <ReferralStat k="+1 mo" v="Stripe credit to the referrer" />
              <ReferralStat k="+1 mo" v="Stripe credit to the new signup" accent />
              <ReferralStat k="One-tap" v="Mobile text share" />
              <ReferralStat k="∞" v="referrals you can stack" accent />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReferralStat({ k, v, accent }) {
  return (
    <div className={"rounded-xl border p-4 " + (accent ? "bg-white border-amber-300" : "bg-white/70 border-amber-100")}>
      <div className={"font-display font-black text-3xl tracking-tighter " + (accent ? "text-amber-700" : "text-[var(--ink)]")}>{k}</div>
      <div className="text-xs text-[var(--ink-2)] mt-1">{v}</div>
    </div>
  );
}

function FAQ() {
  return (
    <section className="mkt-section" id="faqs" style={{ paddingTop: 0 }}>
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <div className="mkt-eyebrow">FAQs</div>
          <h2 className="mkt-h2">Common questions.</h2>
          <p className="text-[var(--ink-2)] mt-3">Don&apos;t see your question? <Link to="/register" className="text-[var(--ink)] font-semibold underline">Try the platform free</Link> and ask us anything in-app.</p>
        </div>
        <div className="md:col-span-2 divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex items-center justify-between cursor-pointer font-display font-bold text-lg">
                {f.q}
                <span className="ml-4 text-[var(--ink-2)] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-[var(--ink-2)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mkt-section" style={{ paddingTop: 0 }}>
      <div className="relative overflow-hidden rounded-3xl bg-[var(--ink)] text-white p-14 lg:p-20">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_BG}')` }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 80% at 90% 30%, rgba(16,185,129,0.35) 0%, transparent 60%)" }} />
        <div className="relative z-10 max-w-2xl">
          <Sparkles size={28} className="text-[var(--primary)] mb-4" />
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-4">
            Every tool a wholesaler needs. One login. One monthly fee.
          </h2>
          <p className="text-white/75 text-lg mb-8 leading-relaxed">
            Stop losing leads in screenshots and notebooks. Try iSpeedToClose free for 14 days — no credit card required.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="btn-money" data-testid="cta-bottom-register">
              Try free <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="btn-outline" style={{ background: "rgba(255,255,255,0.06)", color: "#fff", borderColor: "rgba(255,255,255,0.2)" }}>
              See the dashboard
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--line)] bg-white">
      <div className="max-w-[1240px] mx-auto px-8 py-12 grid md:grid-cols-5 gap-8 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-7 h-7 rounded-md bg-[var(--ink)] text-white flex items-center justify-center"><AssignFlowLogo size={16} /></span>
            <span className="font-display font-extrabold">iSpeedToClose</span>
          </div>
          <p className="text-[var(--ink-2)] leading-relaxed">Speed is the deal. The all-in-one OS for real estate wholesalers + investors. Built with operators, for operators.</p>
          <div className="mt-6">
            <div className="font-display font-black text-[11px] uppercase tracking-[0.18em] text-[var(--ink)] mb-2">
              Follow on Social
            </div>
            <div className="flex items-center gap-2" data-testid="footer-social-icons">
              <SocialIconLink
                href="https://x.com/iSpeedToClose"
                label="X"
                testid="footer-social-x"
                svg={<XLogoSvg />}
              />
              <SocialIconLink
                href="https://facebook.com/iSpeedToClose"
                label="Facebook"
                testid="footer-social-facebook"
                svg={<Facebook size={14} />}
              />
              <SocialIconLink
                href="https://instagram.com/iSpeedToClose"
                label="Instagram"
                testid="footer-social-instagram"
                svg={<Instagram size={14} />}
              />
              <SocialIconLink
                href="https://youtube.com/@iSpeedToClose"
                label="YouTube"
                testid="footer-social-youtube"
                svg={<Youtube size={14} />}
              />            </div>
          </div>
        </div>
        <FooterCol title="Platform" items={[
          { label: "Discover", to: "/v3/crm/discover" },
          { label: "Deal Alerts", to: "/v3/alerts" },
          { label: "Analyzer", to: "/v3/tools/analyzer" },
          { label: "Pipeline", to: "/v3/crm/pipeline" },
          { label: "Portfolio", to: "/v3/tools/portfolio" },
          { label: "Markets", to: "/v3/markets" },
          { label: "Power Team", to: "/v3/business/team" },
          { label: "Refer & Earn", to: "/register" },
          { label: "Affiliate Program", to: "/affiliates" },
        ]} />
        <FooterCol title="Resources" items={[
          { label: "Free 25-page Ebook", to: "/free-ebook" },
          { label: "Resource Library", to: "/resources" },
          { label: "Field Notes", href: "#field-notes" },
          { label: "Community Forum", to: "/forum" },
          { label: "Compare to Alternatives", to: "/compare" },
        ]} />
        <FooterCol title="Company" items={[
          { label: "Pricing", href: "#pricing" },
          { label: "Platform", href: "#modules" },
          { label: "FAQs", href: "#faqs" },
          { label: "Sign in", to: "/login" },
        ]} />
        <FooterCol title="Legal" items={[
          { label: "Terms", to: "/legal/terms" },
          { label: "Privacy", to: "/legal/privacy" },
          { label: "SMS Terms & Consent", to: "/legal/sms-consent" },
          { label: "Messaging Policy", to: "/legal/messaging-policy" },
          { label: "Disclaimer", to: "/legal/disclaimer" },
          { label: "Contact", href: "mailto:hello@ispeedtoclose.com" },
        ]} />
      </div>
      <div className="border-t border-[var(--line)]">
        <div className="max-w-[1240px] mx-auto px-8 py-6 text-xs text-[var(--ink-2)] flex flex-wrap items-center justify-between gap-3">
          <div>
            <div>© {new Date().getFullYear()} iSpeedToClose · A Perrin Properties, LLC product.</div>
            <div className="mt-1 text-[11px] text-[var(--ink-3)]" data-testid="footer-mailing-address">
              45 E City Ave, PMB 721 · Bala Cynwyd, PA 19004
            </div>
          </div>
          <div className="flex items-center gap-1"><Compass size={11} /> Built with care in the field.</div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="font-display font-bold mb-3">{title}</div>
      <ul className="space-y-2 text-[var(--ink-2)]">
        {items.map((i) => (
          <li key={i.label}>
            {i.to ? (
              <Link
                to={i.to}
                className="hover:text-[var(--ink)] transition-colors"
                data-testid={`footer-link-${i.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {i.label}
              </Link>
            ) : (
              <a
                href={i.href}
                target={i.external ? "_blank" : undefined}
                rel={i.external ? "noopener noreferrer" : undefined}
                className="hover:text-[var(--ink)] transition-colors"
                data-testid={`footer-link-${i.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {i.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


// The current X (Twitter) mark. Lucide's `Twitter` icon still ships the
// old bird — we render the current stylised X logo inline instead.
function XLogoSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

function SocialIconLink({ href, label, testid, svg }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Follow iSpeedToClose on ${label}`}
      title={label}
      data-testid={testid}
      className="w-8 h-8 flex items-center justify-center rounded-full border border-[var(--line)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/8 transition-colors"
    >
      {svg}
    </a>
  );
}
